import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiArrowLeft,
  FiAward,
  FiCalendar,
  FiCheckCircle,
  FiCreditCard,
  FiFileText,
  FiHelpCircle,
  FiPlusCircle,
  FiRefreshCw,
  FiShoppingCart,
  FiTrendingUp,
  FiAlertTriangle,
  FiZap,
  FiCamera,
} from 'react-icons/fi';
import { AuthContext } from '../context/AuthContext';
import { ThemeContext } from '../context/ThemeContext';
import { useApi } from '../hooks/useApi';
import { FormAlert } from '../components/form';
import { getUserPlanInfo, getPlanBadgeClasses } from '../utils/planUtils';
import { startOverageCheckout, startOcrCheckout } from '../utils/mercadopagoCheckout';
import { canBuyOcrPacks } from '../utils/ocrAccess';
import { OCR_SCAN_BETA_MESSAGE } from '../constants/ocrBeta';
import { isReadOnlyUser } from '../utils/access';

const RECOMMENDED_PACK = 'pack_100';
const RECOMMENDED_OCR_PACK = 'ocr_50';

const STATUS_COPY = {
  ok: { label: 'En buen estado', tone: 'ok' },
  warn: { label: 'Cerca del límite', tone: 'warn' },
  full: { label: 'Límite alcanzado', tone: 'full' },
  unlimited: { label: 'Sin tope mensual', tone: 'unlimited' },
};

function getStatus(usage) {
  if (!usage) return STATUS_COPY.ok;
  if (usage.unlimited) return STATUS_COPY.unlimited;
  const p = usage.percent ?? 0;
  if (p >= 100) return STATUS_COPY.full;
  if (p >= 80) return STATUS_COPY.warn;
  return STATUS_COPY.ok;
}

function ringColor(tone, modoOscuro) {
  if (tone === 'full') return '#ef4444';
  if (tone === 'warn') return '#f59e0b';
  if (tone === 'unlimited') return '#1F80FF';
  return modoOscuro ? '#1F80FF' : '#004AAD';
}

function UsageRing({ percent, tone, modoOscuro, children }) {
  const size = 132;
  const stroke = 10;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const safePercent = Math.min(Math.max(percent ?? 0, 0), 100);
  const offset = circumference - (safePercent / 100) * circumference;

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={modoOscuro ? '#374151' : '#e5e7eb'}
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={ringColor(tone, modoOscuro)}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={tone === 'unlimited' ? circumference * 0.15 : offset}
          className="transition-all duration-700 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-2">
        {children}
      </div>
    </div>
  );
}

function Skeleton({ modoOscuro }) {
  const bone = modoOscuro ? 'bg-gray-800' : 'bg-gray-200';
  return (
    <div className="space-y-4 animate-pulse">
      <div className={`h-40 rounded-2xl ${bone}`} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className={`h-28 rounded-xl ${bone}`} />
        <div className={`h-28 rounded-xl ${bone}`} />
        <div className={`h-28 rounded-xl ${bone}`} />
      </div>
    </div>
  );
}

const UsoPlan = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { modoOscuro } = useContext(ThemeContext);
  const { secureFetch } = useApi();
  const readOnly = isReadOnlyUser(user);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(null);
  const [error, setError] = useState('');
  const [usage, setUsage] = useState(null);
  const [options, setOptions] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [ocrUsage, setOcrUsage] = useState(null);
  const [ocrOptions, setOcrOptions] = useState([]);
  const [ocrPurchases, setOcrPurchases] = useState([]);
  const [ocrScanEnabled, setOcrScanEnabled] = useState(false);
  const [mpConfigured, setMpConfigured] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const planInfo = useMemo(() => getUserPlanInfo(user), [user]);
  const status = useMemo(() => getStatus(usage), [usage]);

  const box = modoOscuro ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200';
  const textPri = modoOscuro ? 'text-white' : 'text-gray-900';
  const textSec = modoOscuro ? 'text-gray-300' : 'text-gray-600';
  const textMuted = modoOscuro ? 'text-gray-400' : 'text-gray-500';
  const sectionBg = modoOscuro ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200';
  const tileBg = modoOscuro ? 'bg-gray-800/80' : 'bg-white';

  const loadUsage = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await secureFetch('/api/payments/overage/options');
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'No se pudo cargar el uso del plan');
      setUsage(data.usage);
      setOptions(data.options || []);
      setPurchases(data.purchases || []);
      setOcrUsage(data.ocrUsage || null);
      setOcrOptions(data.ocrOptions || []);
      setOcrPurchases(data.ocrPurchases || []);
      setOcrScanEnabled(data.ocrScanEnabled === true);
      setMpConfigured(data.mercadopagoConfigured !== false);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err.message || 'Error al cargar el uso del plan');
    } finally {
      setLoading(false);
    }
  }, [secureFetch]);

  useEffect(() => {
    loadUsage();
  }, [loadUsage]);

  const handleBuyPack = async (packId) => {
    setSubmitting(packId);
    setError('');
    try {
      await startOverageCheckout({ request: secureFetch, packId });
    } catch (err) {
      setError(err.message || 'No pudimos iniciar el pago');
      setSubmitting(null);
    }
  };

  const handleBuyOcrPack = async (packId) => {
    setSubmitting(packId);
    setError('');
    try {
      await startOcrCheckout({ request: secureFetch, packId });
    } catch (err) {
      setError(err.message || 'No pudimos iniciar el pago de escaneos');
      setSubmitting(null);
    }
  };

  const resetLabel = usage?.resetDate
    ? new Date(`${usage.resetDate}T12:00:00`).toLocaleDateString('es-AR', {
        day: 'numeric',
        month: 'long',
      })
    : null;

  const percent = usage?.percent ?? 0;
  const barWidth = usage?.unlimited ? 0 : Math.min(Math.max(percent, 0), 100);
  const ocrPercent = ocrUsage?.percent ?? 0;
  const ocrBarWidth = Math.min(Math.max(ocrPercent, 0), 100);
  const ocrStatus = ocrUsage
    ? (ocrUsage.remaining === 0
      ? STATUS_COPY.full
      : ocrPercent >= 80
        ? STATUS_COPY.warn
        : STATUS_COPY.ok)
    : STATUS_COPY.ok;
  const ocrResetLabel = ocrUsage?.resetDate
    ? new Date(`${ocrUsage.resetDate}T12:00:00`).toLocaleDateString('es-AR', { day: 'numeric', month: 'long' })
    : null;
  const showOcrPacks = canBuyOcrPacks(user) && ocrOptions.length > 0;
  const ocrBetaPaused = Boolean(ocrUsage) && !ocrScanEnabled;

  const statusBadgeClass = {
    ok: modoOscuro ? 'bg-emerald-950/50 text-emerald-300 border-emerald-800' : 'bg-emerald-50 text-emerald-800 border-emerald-200',
    warn: modoOscuro ? 'bg-amber-950/50 text-amber-300 border-amber-800' : 'bg-amber-50 text-amber-800 border-amber-200',
    full: modoOscuro ? 'bg-red-950/50 text-red-300 border-red-800' : 'bg-red-50 text-red-700 border-red-200',
    unlimited: modoOscuro ? 'bg-blue-950/50 text-blue-300 border-blue-800' : 'bg-blue-50 text-blue-800 border-blue-200',
  }[status.tone];

  const formatPurchaseDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('es-AR', { day: 'numeric', month: 'short' });
  };

  const purchaseStatusLabel = (s) => {
    const map = { approved: 'Acreditado', pending: 'Pendiente', rejected: 'Rechazado' };
    return map[String(s).toLowerCase()] || s;
  };

  return (
    <div className={modoOscuro ? 'w-full min-h-[calc(100dvh-4rem)] bg-black text-white' : 'w-full min-h-[calc(100dvh-4rem)] bg-[#f5f5f7] text-gray-900'}>
      <div className="w-full max-w-[85rem] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-12 py-4 sm:py-5 pb-12 space-y-4 sm:space-y-5">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <button
              type="button"
              onClick={() => navigate('/inicio')}
              className={`inline-flex items-center gap-1.5 text-xs font-medium mb-2 transition-colors ${textMuted} hover:opacity-80`}
            >
              <FiArrowLeft className="w-3.5 h-3.5" />
              Volver a Inicio
            </button>
            <h1 className={`text-xl sm:text-2xl lg:text-3xl font-semibold leading-tight ${textPri}`}>
              Uso del plan
            </h1>
            <div className={`flex flex-wrap items-center gap-x-2 gap-y-1.5 mt-1.5 text-sm ${textSec}`}>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${getPlanBadgeClasses(planInfo.slug, modoOscuro)}`}>
                <FiAward className="w-3 h-3 shrink-0" />
                Plan {planInfo.name}
              </span>
              {usage?.periodLabel && (
                <>
                  <span className={textMuted}>·</span>
                  <span className="capitalize">{usage.periodLabel}</span>
                </>
              )}
              {lastUpdated && (
                <>
                  <span className={textMuted}>·</span>
                  <span className={`text-xs ${textMuted}`}>
                    Actualizado {lastUpdated.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={loadUsage}
            disabled={loading}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border disabled:opacity-50 shrink-0 ${
              modoOscuro ? 'border-gray-700 bg-gray-900 hover:bg-gray-800' : 'border-gray-200 bg-white hover:bg-gray-50'
            }`}
          >
            <FiRefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </button>
        </header>

        <FormAlert type="error">{error}</FormAlert>

        {loading ? (
          <Skeleton modoOscuro={modoOscuro} />
        ) : usage ? (
          <>
            {status.tone === 'full' && !usage.unlimited && (
              <FormAlert type="warning" action={
                <button
                  type="button"
                  onClick={() => document.getElementById('packs-section')?.scrollIntoView({ behavior: 'smooth' })}
                  className="text-xs font-semibold underline underline-offset-2 shrink-0"
                >
                  Ver packs
                </button>
              }>
                Alcanzaste el límite de registros de venta de este mes. Comprá un pack extra o subí de plan para seguir cargando.
              </FormAlert>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
              <section className={`lg:col-span-7 rounded-2xl border p-4 sm:p-5 ${box}`}>
                <div className="flex flex-col sm:flex-row gap-5 sm:gap-6 items-center sm:items-start">
                  <UsageRing percent={usage.unlimited ? 100 : percent} tone={status.tone} modoOscuro={modoOscuro}>
                    {usage.unlimited ? (
                      <>
                        <span className={`text-2xl font-bold tabular-nums ${textPri}`}>{usage.used}</span>
                        <span className={`text-[10px] uppercase tracking-wide mt-0.5 ${textMuted}`}>registros</span>
                      </>
                    ) : (
                      <>
                        <span className={`text-2xl font-bold tabular-nums ${textPri}`}>{percent}%</span>
                        <span className={`text-[10px] uppercase tracking-wide mt-0.5 ${textMuted}`}>del cupo</span>
                      </>
                    )}
                  </UsageRing>

                  <div className="flex-1 min-w-0 text-center sm:text-left">
                    <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 mb-2">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold border ${statusBadgeClass}`}>
                        {status.tone === 'full' ? <FiAlertTriangle className="w-3.5 h-3.5" /> : status.tone === 'warn' ? <FiTrendingUp className="w-3.5 h-3.5" /> : <FiCheckCircle className="w-3.5 h-3.5" />}
                        {status.label}
                      </span>
                    </div>

                    <p className={`text-[11px] font-semibold uppercase tracking-wide ${textMuted}`}>
                      Registros de venta · mes actual
                    </p>
                    <p className={`text-2xl sm:text-3xl font-semibold tabular-nums mt-0.5 ${textPri}`}>
                      {usage.unlimited ? (
                        usage.used
                      ) : (
                        <>
                          {usage.used}
                          <span className={`text-lg font-medium ${textMuted}`}> / {usage.effectiveLimit}</span>
                        </>
                      )}
                    </p>
                    <p className={`text-xs sm:text-sm mt-1.5 leading-relaxed ${textSec}`}>
                      {usage.unlimited
                        ? 'Tu plan Empresarial no tiene límite mensual de comprobantes de venta.'
                        : `${usage.remaining ?? 0} registros disponibles${usage.extraDocuments > 0 ? ` · +${usage.extraDocuments} comprados este mes` : ''}${resetLabel ? ` · se renueva el ${resetLabel}` : ''}.`}
                    </p>

                    {!usage.unlimited && (
                      <div className="mt-4">
                        <div className={`h-2.5 rounded-full overflow-hidden ${modoOscuro ? 'bg-gray-800' : 'bg-gray-200'}`}>
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${
                              percent >= 100 ? 'bg-red-500' : percent >= 80 ? 'bg-amber-500' : 'bg-[#1F80FF]'
                            }`}
                            style={{ width: `${barWidth}%` }}
                          />
                        </div>
                        <div className="flex justify-between text-[10px] mt-1 tabular-nums">
                          <span className={textMuted}>0</span>
                          <span className={textMuted}>{usage.effectiveLimit}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {!usage.unlimited && (
                  <div className="grid grid-cols-3 gap-2 sm:gap-3 mt-5 pt-5 border-t border-dashed border-gray-600/30">
                    {[
                      { label: 'Base del plan', value: usage.baseLimit ?? '—', hint: 'incluido' },
                      { label: 'Extra comprados', value: `+${usage.extraDocuments || 0}`, hint: 'este mes' },
                      { label: 'Tope efectivo', value: usage.effectiveLimit ?? '—', hint: 'total' },
                    ].map((item) => (
                      <div key={item.label} className={`rounded-xl px-3 py-2.5 border ${sectionBg}`}>
                        <p className={`text-[10px] uppercase tracking-wide ${textMuted}`}>{item.label}</p>
                        <p className={`text-lg font-semibold tabular-nums ${textPri}`}>{item.value}</p>
                        <p className={`text-[10px] ${textMuted}`}>{item.hint}</p>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <aside className="lg:col-span-5 space-y-4">
                <div className={`rounded-2xl border p-4 ${box}`}>
                  <p className={`text-[11px] font-semibold uppercase tracking-wide mb-3 ${textMuted}`}>Acciones rápidas</p>
                  <div className="space-y-2">
                    {!readOnly && (
                      <button
                        type="button"
                        onClick={() => navigate('/facturas?tab=crear&modo=rapido')}
                        className={`w-full flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors hover:border-[#1F80FF] ${sectionBg}`}
                      >
                        <span className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${modoOscuro ? 'bg-[#1F80FF]/20 text-[#5CA3FF]' : 'bg-[#1F80FF]/10 text-[#004AAD]'}`}>
                          <FiFileText className="w-4 h-4" />
                        </span>
                        <span>
                          <span className={`block text-sm font-semibold ${textPri}`}>Venta rápida</span>
                          <span className={`block text-xs ${textMuted}`}>Registrá una venta en pocos pasos</span>
                        </span>
                      </button>
                    )}
                    {!usage.unlimited && (
                      <button
                        type="button"
                        onClick={() => document.getElementById('packs-section')?.scrollIntoView({ behavior: 'smooth' })}
                        className={`w-full flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors hover:border-[#1F80FF] ${sectionBg}`}
                      >
                        <span className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${modoOscuro ? 'bg-[#1F80FF]/20 text-[#5CA3FF]' : 'bg-[#1F80FF]/10 text-[#004AAD]'}`}>
                          <FiPlusCircle className="w-4 h-4" />
                        </span>
                        <span>
                          <span className={`block text-sm font-semibold ${textPri}`}>Comprar registros</span>
                          <span className={`block text-xs ${textMuted}`}>Pago único este mes</span>
                        </span>
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => navigate('/planes')}
                      className={`w-full flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors hover:border-[#1F80FF] ${sectionBg}`}
                    >
                      <span className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${modoOscuro ? 'bg-[#1F80FF]/20 text-[#5CA3FF]' : 'bg-[#1F80FF]/10 text-[#004AAD]'}`}>
                        <FiCreditCard className="w-4 h-4" />
                      </span>
                      <span>
                        <span className={`block text-sm font-semibold ${textPri}`}>Ver planes</span>
                        <span className={`block text-xs ${textMuted}`}>Más cupo cada mes</span>
                      </span>
                    </button>
                  </div>
                </div>

                <div className={`rounded-2xl border p-4 ${box}`}>
                  <div className="flex items-start gap-2.5">
                    <FiHelpCircle className={`w-4 h-4 mt-0.5 shrink-0 ${modoOscuro ? 'text-[#5CA3FF]' : 'text-[#004AAD]'}`} />
                    <div>
                      <p className={`text-sm font-semibold ${textPri}`}>¿Qué cuenta?</p>
                      <ul className={`mt-2 space-y-1.5 text-xs leading-relaxed ${textSec}`}>
                        <li>Cada <strong className={textPri}>comprobante de venta</strong> creado en el mes suma 1 al cupo.</li>
                        <li>Los packs extra valen solo hasta fin de mes; el {resetLabel || 'día 1'} volvés al cupo base.</li>
                        <li>Editar o borrar una venta no devuelve cupo del mes en que se creó.</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {purchases.length > 0 && (
                  <div className={`rounded-2xl border p-4 ${box}`}>
                    <p className={`text-[11px] font-semibold uppercase tracking-wide mb-3 ${textMuted}`}>Compras del mes</p>
                    <ul className="space-y-2">
                      {purchases.map((p) => (
                        <li key={p.id} className={`flex items-center justify-between gap-2 rounded-lg px-3 py-2 border ${sectionBg}`}>
                          <div className="min-w-0">
                            <p className={`text-sm font-medium ${textPri}`}>+{p.documents} registros</p>
                            <p className={`text-[11px] ${textMuted}`}>{formatPurchaseDate(p.createdAt)}</p>
                          </div>
                          <div className="text-right shrink-0">
                            <p className={`text-xs font-semibold tabular-nums ${textPri}`}>
                              ${Number(p.amount).toLocaleString('es-AR')}
                            </p>
                            <p className={`text-[10px] ${
                              p.status === 'approved'
                                ? (modoOscuro ? 'text-emerald-400' : 'text-emerald-700')
                                : textMuted
                            }`}>
                              {purchaseStatusLabel(p.status)}
                            </p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </aside>
            </div>

            {ocrUsage && (
              <section className={`rounded-2xl border p-4 sm:p-5 ${box}`}>
                <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                  <div>
                    <h2 className={`text-base font-semibold flex items-center gap-2 ${textPri}`}>
                      <FiCamera className="w-4 h-4 shrink-0" />
                      Escaneos OCR
                    </h2>
                    <p className={`text-xs sm:text-sm mt-1 ${textSec}`}>
                      {ocrUsage.trialOnly
                        ? 'Plan Básico: 3 lecturas de prueba en total. Subí a Profesional para 40 escaneos por mes.'
                        : 'Escaneo de factura o ticket → egreso prellenado. Confirmás antes de guardar.'}
                    </p>
                  </div>
                  {!ocrUsage.trialOnly && ocrResetLabel && (
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs ${sectionBg} border ${textMuted}`}>
                      <FiCalendar className="w-3.5 h-3.5" />
                      Renueva el {ocrResetLabel}
                    </span>
                  )}
                </div>

                {ocrBetaPaused && (
                  <FormAlert type="info" className="mb-4">
                    {OCR_SCAN_BETA_MESSAGE}
                  </FormAlert>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                  <div className={`lg:col-span-7 rounded-xl border p-4 ${sectionBg}`}>
                    <div className="flex flex-col sm:flex-row gap-4 items-center sm:items-start">
                      <UsageRing percent={ocrPercent} tone={ocrStatus.tone} modoOscuro={modoOscuro}>
                        <span className={`text-2xl font-bold tabular-nums ${textPri}`}>{ocrPercent}%</span>
                        <span className={`text-[10px] uppercase tracking-wide mt-0.5 ${textMuted}`}>usado</span>
                      </UsageRing>
                      <div className="flex-1 min-w-0 text-center sm:text-left">
                        <p className={`text-[11px] font-semibold uppercase tracking-wide ${textMuted}`}>
                          {ocrUsage.trialOnly ? 'Pruebas OCR · total' : 'Escaneos · mes actual'}
                        </p>
                        <p className={`text-2xl font-semibold tabular-nums mt-0.5 ${textPri}`}>
                          {ocrUsage.used}
                          <span className={`text-lg font-medium ${textMuted}`}> / {ocrUsage.effectiveLimit}</span>
                        </p>
                        <p className={`text-xs mt-1.5 ${textSec}`}>
                          {ocrUsage.trialOnly
                            ? `${ocrUsage.trialRemaining ?? 0} prueba(s) restante(s)`
                            : `${ocrUsage.remaining ?? 0} disponibles${ocrUsage.extraScans > 0 ? ` · +${ocrUsage.extraScans} comprados` : ''}`}
                        </p>
                        <div className="mt-3">
                          <div className={`h-2 rounded-full overflow-hidden ${modoOscuro ? 'bg-gray-800' : 'bg-gray-200'}`}>
                            <div
                              className={`h-full rounded-full transition-all duration-500 ${
                                ocrPercent >= 100 ? 'bg-red-500' : ocrPercent >= 80 ? 'bg-amber-500' : 'bg-[#1F80FF]'
                              }`}
                              style={{ width: `${ocrBarWidth}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <aside className="lg:col-span-5 space-y-3">
                    <button
                      type="button"
                      onClick={() => navigate('/egresos?tab=ticket')}
                      className={`w-full flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors hover:border-[#1F80FF] ${sectionBg}`}
                    >
                      <span className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${modoOscuro ? 'bg-[#1F80FF]/20 text-[#5CA3FF]' : 'bg-[#1F80FF]/10 text-[#004AAD]'}`}>
                        <FiCamera className="w-4 h-4" />
                      </span>
                      <span>
                        <span className={`block text-sm font-semibold ${textPri}`}>Escanear comprobante</span>
                        <span className={`block text-xs ${textMuted}`}>
                          {ocrBetaPaused ? 'Próximamente en la beta' : 'Ir a Egresos → Escanear ticket'}
                        </span>
                      </span>
                    </button>
                    {showOcrPacks && (
                      <button
                        type="button"
                        onClick={() => document.getElementById('ocr-packs')?.scrollIntoView({ behavior: 'smooth' })}
                        className={`w-full flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors hover:border-[#1F80FF] ${sectionBg}`}
                      >
                        <span className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${modoOscuro ? 'bg-[#1F80FF]/20 text-[#5CA3FF]' : 'bg-[#1F80FF]/10 text-[#004AAD]'}`}>
                          <FiPlusCircle className="w-4 h-4" />
                        </span>
                        <span>
                          <span className={`block text-sm font-semibold ${textPri}`}>Comprar escaneos</span>
                          <span className={`block text-xs ${textMuted}`}>Packs desde $3.500</span>
                        </span>
                      </button>
                    )}
                  </aside>
                </div>

                {ocrPurchases.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-dashed border-gray-600/30">
                    <p className={`text-[11px] font-semibold uppercase tracking-wide mb-2 ${textMuted}`}>Compras OCR del mes</p>
                    <ul className="space-y-2">
                      {ocrPurchases.map((p) => (
                        <li key={p.id} className={`flex items-center justify-between gap-2 rounded-lg px-3 py-2 border ${sectionBg}`}>
                          <div className="min-w-0">
                            <p className={`text-sm font-medium ${textPri}`}>+{p.scans} escaneos</p>
                            <p className={`text-[11px] ${textMuted}`}>{formatPurchaseDate(p.createdAt)}</p>
                          </div>
                          <div className="text-right shrink-0">
                            <p className={`text-xs font-semibold tabular-nums ${textPri}`}>
                              ${Number(p.amount).toLocaleString('es-AR')}
                            </p>
                            <p className={`text-[10px] ${p.status === 'approved' ? (modoOscuro ? 'text-emerald-400' : 'text-emerald-700') : textMuted}`}>
                              {purchaseStatusLabel(p.status)}
                            </p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </section>
            )}

            {showOcrPacks && (
              <section id="ocr-packs" className={`rounded-2xl border p-4 sm:p-5 ${box}`}>
                <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                  <div>
                    <h2 className={`text-base font-semibold ${textPri}`}>Comprar más escaneos OCR</h2>
                    <p className={`text-xs sm:text-sm mt-1 ${textSec}`}>
                      Pago único con Mercado Pago. Se suman al cupo de {ocrUsage?.periodLabel || 'este mes'}.
                    </p>
                  </div>
                </div>

                {!mpConfigured && (
                  <FormAlert type="warning" className="mb-4">
                    Mercado Pago no está configurado. Contactá al administrador para habilitar compras.
                  </FormAlert>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {ocrOptions.map((pack) => {
                    const isRecommended = pack.id === RECOMMENDED_OCR_PACK;
                    const pricePerScan = pack.scans ? Math.round(Number(pack.price) / pack.scans) : 0;
                    const isLoading = submitting === pack.id;

                    return (
                      <div
                        key={pack.id}
                        className={`relative rounded-2xl border p-4 flex flex-col h-full transition-shadow ${
                          isRecommended
                            ? (modoOscuro ? 'border-[#1F80FF] shadow-lg shadow-blue-950/30' : 'border-[#1F80FF] shadow-md')
                            : sectionBg
                        } ${tileBg}`}
                      >
                        {isRecommended && (
                          <span className="absolute -top-2.5 left-4 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-[#1F80FF] text-white">
                            <FiZap className="w-3 h-3" />
                            Recomendado
                          </span>
                        )}
                        <p className={`text-lg font-bold ${textPri}`}>{pack.label}</p>
                        <p className={`text-xs mt-1.5 leading-relaxed flex-1 ${textSec}`}>{pack.description}</p>
                        <div className="mt-4 pt-3 border-t border-dashed border-gray-500/20">
                          <p className={`text-2xl font-bold tabular-nums ${textPri}`}>
                            ${Number(pack.price).toLocaleString('es-AR')}
                          </p>
                          <p className={`text-[11px] mt-0.5 ${textMuted}`}>
                            ~${pricePerScan.toLocaleString('es-AR')} por escaneo · pago único
                          </p>
                        </div>
                        <button
                          type="button"
                          disabled={Boolean(submitting) || !mpConfigured}
                          onClick={() => handleBuyOcrPack(pack.id)}
                          className={`mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors disabled:opacity-60 ${
                            isRecommended
                              ? 'bg-[#1F80FF] text-white hover:bg-[#004AAD]'
                              : (modoOscuro ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-900 text-white hover:bg-black')
                          }`}
                        >
                          {isLoading ? (
                            <FiRefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <FiShoppingCart className="w-4 h-4" />
                          )}
                          {isLoading ? 'Redirigiendo…' : 'Comprar'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {!usage.unlimited && (
              <section id="packs-section" className={`rounded-2xl border p-4 sm:p-5 ${box}`}>
                <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                  <div>
                    <h2 className={`text-base font-semibold ${textPri}`}>Comprar más registros</h2>
                    <p className={`text-xs sm:text-sm mt-1 ${textSec}`}>
                      Pago único con Mercado Pago. Se suman al cupo de {usage.periodLabel || 'este mes'}.
                    </p>
                  </div>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs ${sectionBg} border ${textMuted}`}>
                    <FiCalendar className="w-3.5 h-3.5" />
                    Renueva {resetLabel ? `el ${resetLabel}` : 'cada mes'}
                  </span>
                </div>

                {!mpConfigured && (
                  <FormAlert type="warning" className="mb-4">
                    Mercado Pago no está configurado. Contactá al administrador para habilitar compras.
                  </FormAlert>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {options.map((pack) => {
                    const isRecommended = pack.id === RECOMMENDED_PACK;
                    const pricePerDoc = pack.documents ? Math.round(Number(pack.price) / pack.documents) : 0;
                    const isLoading = submitting === pack.id;

                    return (
                      <div
                        key={pack.id}
                        className={`relative rounded-2xl border p-4 flex flex-col h-full transition-shadow ${
                          isRecommended
                            ? (modoOscuro ? 'border-[#1F80FF] shadow-lg shadow-blue-950/30' : 'border-[#1F80FF] shadow-md')
                            : sectionBg
                        } ${tileBg}`}
                      >
                        {isRecommended && (
                          <span className="absolute -top-2.5 left-4 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-[#1F80FF] text-white">
                            <FiZap className="w-3 h-3" />
                            Recomendado
                          </span>
                        )}
                        <p className={`text-lg font-bold ${textPri}`}>{pack.label}</p>
                        <p className={`text-xs mt-1.5 leading-relaxed flex-1 ${textSec}`}>{pack.description}</p>
                        <div className="mt-4 pt-3 border-t border-dashed border-gray-500/20">
                          <p className={`text-2xl font-bold tabular-nums ${textPri}`}>
                            ${Number(pack.price).toLocaleString('es-AR')}
                          </p>
                          <p className={`text-[11px] mt-0.5 ${textMuted}`}>
                            ~${pricePerDoc.toLocaleString('es-AR')} por registro · pago único
                          </p>
                        </div>
                        <button
                          type="button"
                          disabled={Boolean(submitting) || !mpConfigured}
                          onClick={() => handleBuyPack(pack.id)}
                          className={`mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors disabled:opacity-60 ${
                            isRecommended
                              ? 'bg-[#1F80FF] text-white hover:bg-[#004AAD]'
                              : (modoOscuro ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-900 text-white hover:bg-black')
                          }`}
                        >
                          {isLoading ? (
                            <FiRefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <FiShoppingCart className="w-4 h-4" />
                          )}
                          {isLoading ? 'Redirigiendo…' : 'Comprar'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            <section className={`rounded-2xl border overflow-hidden ${box}`}>
              <div className={`px-4 sm:px-5 py-4 border-b ${modoOscuro ? 'border-gray-700 bg-gradient-to-r from-gray-900 to-gray-900/80' : 'border-gray-200 bg-gradient-to-r from-[#1F80FF]/5 to-transparent'}`}>
                <h2 className={`text-sm font-semibold ${textPri}`}>¿Necesitás más capacidad todos los meses?</h2>
                <p className={`text-xs mt-1 max-w-2xl ${textSec}`}>
                  {planInfo.tagline || 'Un plan superior sube el cupo base e incluye estado de resultados, multimoneda, equipo y más.'}
                </p>
              </div>
              <div className="px-4 sm:px-5 py-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => navigate('/planes')}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-[#1F80FF] text-white hover:bg-[#004AAD] transition-colors"
                >
                  <FiCreditCard className="w-4 h-4" />
                  Comparar planes
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/inicio')}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition-colors ${
                    modoOscuro ? 'border-gray-600 hover:bg-gray-800' : 'border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Volver al inicio
                </button>
              </div>
            </section>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default UsoPlan;
